'use client';

import { DEFAULT_TTS_SETTINGS, readTTSSettings, TTSSettings } from '../utils/ttsSettings';
import { mediaQueries } from '../utils/mediaQueries';
import { useMediaQuery } from '../hooks/useMediaQuery';

import { createContext, use, useContext, useState, useEffect, useLayoutEffect, ReactNode } from 'react';

export interface Settings {
  tts: TTSSettings;
  fontSize: 'small' | 'medium' | 'large' | 'x-large';
  fontFamily: string;
  darkMode: 'light' | 'dark' | 'auto';
  /** Allow section activation to start video; explicit play/link actions remain available. */
  autoplayVideos: boolean;
  /** Enable the app's left/right edge navigation targets. */
  tapToNavigate: boolean;
}

interface SettingsContextType {
  settings: Settings;
  /** Playback must wait for persisted preferences instead of acting on SSR defaults. */
  preferencesLoaded: boolean;
  updateSettings: (newSettings: Partial<Settings>) => void;
}

const defaultSettings: Settings = {
  tts: DEFAULT_TTS_SETTINGS,
  fontSize: 'medium',
  fontFamily: 'Source_Serif_4',
  darkMode: 'auto',
  autoplayVideos: true,
  tapToNavigate: true,
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Merge saved settings (strip legacy keys like ttsSpeed that are no longer in Settings).
// Only read from localStorage after mount so the first client render matches SSR (defaults).
const readStoredSettings = (): Settings => {
  const savedSettings = localStorage.getItem('userSettings');
  if (!savedSettings) {
    return defaultSettings;
  }
  try {
    const parsed = JSON.parse(savedSettings);
    const { ttsSpeed: _removed, removeTapToScrollSidebars: legacyRemoveTap, ...rest } = parsed;
    return {
      ...defaultSettings, ...rest, tts: readTTSSettings(rest.tts),
      autoplayVideos: typeof rest.autoplayVideos === 'boolean' ? rest.autoplayVideos : true,
      tapToNavigate: typeof rest.tapToNavigate === 'boolean' ? rest.tapToNavigate
        : typeof legacyRemoveTap === 'boolean' ? !legacyRemoveTap : true,
    };
  } catch {
    return defaultSettings;
  }
};

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const prefersDark = useMediaQuery(mediaQueries.darkColorScheme);

  // Hydrate from localStorage before paint to avoid a visible flash while matching server HTML
  // for the initial render (prevents hydration mismatches in descendants that branch on settings).
  useLayoutEffect(() => {
    setSettings(readStoredSettings());
    setPreferencesLoaded(true);
  }, []);

  useEffect(() => {
    // Handle font classes
    const fontFamilyClasses = Array.from(document.body.classList)
      .filter(className => className.startsWith('font-'));
    const fontSizeClasses = Array.from(document.body.classList)
      .filter(className => className.startsWith('text-size-'));
    document.body.classList.remove(...fontFamilyClasses, ...fontSizeClasses);

    const fontClass = `font-${settings.fontFamily.split('_')[0].toLowerCase()}`;
    document.body.classList.add(fontClass);
    document.body.classList.add(`text-size-${settings.fontSize}`);

    // Handle dark mode
    const rootElement = document.documentElement;
    const isDark = rootElement.hasAttribute('data-force-dark') || settings.darkMode === 'dark' ||
      (settings.darkMode === 'auto' && prefersDark);
    rootElement.classList.toggle('dark', isDark);
  }, [settings.fontFamily, settings.fontSize, settings.darkMode, prefersDark]);

  const updateSettings = (newSettings: Partial<Settings>) => {
    setSettings(prev => {
      const updated = { ...prev, ...newSettings };
      localStorage.setItem('userSettings', JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <SettingsContext.Provider value={{ settings, preferencesLoaded, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

/** Reading/video components remain usable without a settings provider, as before TTS preferences. */
export function useTTSSettings(enabled = true): TTSSettings {
  // React 19's use(Context) can be conditional: TTS-off components must not gain
  // preference subscriptions that restart ordinary video polling on menu edits.
  const context = enabled ? use(SettingsContext) : undefined;
  return context?.settings.tts ?? DEFAULT_TTS_SETTINGS;
}

/** Standalone players without settings retain the default automatic playback policy. */
export function useAutoplayVideos(): boolean {
  const context = useContext(SettingsContext);
  return context ? context.preferencesLoaded && context.settings.autoplayVideos : true;
}
